import { useIdleTimer } from '@/hooks/useIdleTimer';
import { useAuthState } from '@/hooks/useAuthState';
import { useStatusModal } from '@/hooks/useStatusModal';
import { useDashboardIntegration } from '@/hooks/useDashboardIntegration';
import { StatusModal } from '@/components/ui/status-modal';
import { useEffect, useRef, useMemo } from 'react';
import { ReactNode } from 'react';
import SessionExpiryTimer from '@/lib/sessionExpiryTimer';
import { getTokenExpirationDurationMinutes, refreshAccessToken } from '@/lib/tokenRefresh';
import { RouteGuard } from '@/components/guards/RouteGuard';

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const { isAuthenticated, access_token } = useAuthState();
  const { modalState, showSessionExpired, closeModal, showIdleWarning } = useStatusModal();

  // Calculate idle timeout from token expiration duration
  const idleTimeoutMinutes = useMemo(() => {
    if (!isAuthenticated || !access_token) return null;
    const duration = getTokenExpirationDurationMinutes(access_token);
    return duration && duration > 0 ? duration : null;
  }, [isAuthenticated, access_token]);

  // Initialize dashboard and menu integration
  const {
    user,
    currentDashboard,
    menuConfig,
    dashboardLoading,
    menuLoading
  } = useDashboardIntegration();

  // Create a ref to store the modal functions so they can be accessed by the idle timer
  const modalRef = useRef({ showIdleWarning, closeModal });
  const sessionExpiredModalRef = useRef({ showSessionExpired });

  // Update the ref when modal functions change
  useEffect(() => {
    modalRef.current = { showIdleWarning, closeModal };
  }, [showIdleWarning, closeModal]);

  useEffect(() => {
    sessionExpiredModalRef.current = { showSessionExpired };
  }, [showSessionExpired]);

  // Always call the hook with token-based timeout
  useIdleTimer(idleTimeoutMinutes, isAuthenticated, modalRef);

  // Set up session manager callback
  useEffect(() => {
    if (!isAuthenticated) {
      SessionExpiryTimer.clearTimers();
      SessionExpiryTimer.setIdleLogoutInProgress(false);
      return;
    }

    SessionExpiryTimer.registerModalCallback(() => {
      showSessionExpired(() => window.location.href = "/login", async () => {
        const refreshed = await refreshAccessToken();
        if (refreshed && refreshed.access_token) {
          // Success - restart timer
          SessionExpiryTimer.clearTimers();
          SessionExpiryTimer.startSessionExpiry();
          return;
        }
      });
    });

    SessionExpiryTimer.startSessionExpiry();

    return () => {
      SessionExpiryTimer.clearTimers();
      SessionExpiryTimer.setIdleLogoutInProgress(false);
    };
  }, [isAuthenticated, showSessionExpired]);


  return (
    <>
      <RouteGuard>
        {children}
      </RouteGuard>
      <StatusModal {...modalState} onClose={closeModal} />
    </>
  );
}